import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  MaterialRequestsActions,
  ClearAndFetchWarehouseMaterialRequests,
  ClearAndFetchWarehouseMaterialRequestsSuccess,
  MaterialRequestsNoAction,
  ClearAndFetchMaterialRequestDetails,
  ClearAndFetchMaterialRequestDetailsSuccess,
  ClearAndFetchMaterialRequestCounts,
  ClearAndFetchMaterialRequestCountsSuccess,
} from './material-requests.actions';
import { catchError, finalize, map, switchMap, take } from 'rxjs/operators';
import { forkJoin, from, of } from 'rxjs';
import { AppNotificationUtilClass, Organizi8DataProvider } from '@monorepo/services';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class MaterialRequestsDBEffects {
  constructor(
    private actions$: Actions,
    private apiHelper: AppNotificationUtilClass,
    private organizi8DataProvider: Organizi8DataProvider,
    private translate: TranslateService,
  ) {}

  ClearAndFetchMaterialRequestCounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ClearAndFetchMaterialRequestCounts>(MaterialRequestsActions.ClearAndFetchMaterialRequestCounts),
      switchMap((action) =>
        from(this.apiHelper.presentLoading(this.translate.instant('organizi8_ngrx_store.loading'))).pipe(
          take(1),
          map((loader) => [loader, action.payload] as const),
        ),
      ),
      switchMap(([loader, payload]) =>
        this.organizi8DataProvider.fetchMaterialRequestCounts(payload).pipe(
          take(1),
          map((res) => new ClearAndFetchMaterialRequestCountsSuccess(res)),
          catchError((error) => {
            console.log('[CLEAR_AND_FETCH_MATERIAL_REQUESTS_COUNT_ERROR]', error);
            return of(new MaterialRequestsNoAction());
          }),
          finalize(() => this.apiHelper.dismissLoadingV2(loader)),
        ),
      ),
    ),
  );

  ClearAndFetchWarehouseMaterialRequests$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ClearAndFetchWarehouseMaterialRequests>(MaterialRequestsActions.ClearAndFetchWarehouseMaterialRequests),
      switchMap((action) =>
        from(
          this.apiHelper.presentLoading(this.translate.instant('organizi8_ngrx_store.getting_material_requests')),
        ).pipe(
          take(1),
          map((loaderEl) => [loaderEl, action.payload] as const),
        ),
      ),
      switchMap(([loaderEl, payload]) =>
        this.organizi8DataProvider.getWarehouseMaterialRequests(payload).pipe(
          take(1),
          map((res) => new ClearAndFetchWarehouseMaterialRequestsSuccess(res)),
          catchError((error) => {
            console.log('[CLEAR_AND_FETCH_WAREHOUSE_MATERIAL_REQUESTS_ERROR]', error);
            return of(new MaterialRequestsNoAction());
          }),
          finalize(() => this.apiHelper.dismissLoadingV2(loaderEl)),
        ),
      ),
    ),
  );

  ClearAndFetchMaterialRequestDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ClearAndFetchMaterialRequestDetails>(MaterialRequestsActions.ClearAndFetchMaterialRequestDetails),
      switchMap((action) =>
        from(
          this.apiHelper.presentLoading(
            this.translate.instant('organizi8_ngrx_store.getting_material_request_details'),
          ),
        ).pipe(
          take(1),
          map((loaderEl) => [loaderEl, action.payload, action.requestStatus] as const),
        ),
      ),
      switchMap(([loaderEl, payload, requestStatus]) => {
        const fabricItemDetails$ =
          requestStatus === 'PENDING'
            ? this.organizi8DataProvider.getFabricItemPendingMaterialRequestDetails(payload)
            : this.organizi8DataProvider.getFabricItemCompletedMaterialRequestDetails(payload);

        const trimItemDetails$ =
          requestStatus === 'PENDING'
            ? this.organizi8DataProvider.getTrimItemPendingMaterialRequestDetails(payload)
            : this.organizi8DataProvider.getTrimItemCompletedMaterialRequestDetails(payload);

        return forkJoin({
          fabricItemDetails: fabricItemDetails$,
          trimItemDetails: trimItemDetails$,
        }).pipe(
          take(1),
          map(({ fabricItemDetails, trimItemDetails }) => {
            if (fabricItemDetails.status === 'SUCCESS' && trimItemDetails.status === 'SUCCESS') {
              console.log({ fabricItemDetails });
              console.log({ trimItemDetails });
              return new ClearAndFetchMaterialRequestDetailsSuccess(
                fabricItemDetails.materialRequestBOMDetails,
                trimItemDetails.materialRequestBOMDetails,
                fabricItemDetails.receiverLocationId || trimItemDetails.receiverLocationId,
              );
            } else {
              this.apiHelper.showPopup(
                'Error',
                fabricItemDetails.errorMessage || trimItemDetails.errorMessage || 'Something went wrong!',
              );
              return new MaterialRequestsNoAction();
            }
          }),
          catchError((error) => {
            console.log('[CLEAR_AND_FETCH_MATERIAL_REQUEST_DETAILS_ERROR]', error);
            return of(new MaterialRequestsNoAction());
          }),
          finalize(() => this.apiHelper.dismissLoadingV2(loaderEl)),
        );
      }),
    ),
  );
}
